import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ProtocolStepDraft } from '@matter_extractor/shared';
import { useId } from 'react';

/**
 * The checkbox « Cacher les objectifs à l'interviewer » (issue #191,
 * `scenario-succes-conduite.md` §4.3, H18; maquette
 * `scenario-succes-conduite/EtapeObjectifsCaches.html`, variante B).
 *
 * Ticked on a step, the interviewer is served the step's direction ONLY: neither
 * the block of open objectives nor the engine's « Il manque encore » directive.
 * The scribe, the statuses, the conditions and the caps do not change - only
 * what the interviewer READS does, which is why the help line says so. The
 * third box of the options list, under the farewell and the reread boxes.
 *
 * ## Closed on a farewell step, and unticked by it
 *
 * « Fermée sur une étape d'adieu, qui n'a pas d'objectifs » (§4.3): the box is
 * DISABLED there with the reason in a bubble, and ticking « Étape d'adieu »
 * unticks it (`setFarewell`), so the editor never writes the pair
 * `protocolProblems` refuses as `hide-objectives-on-farewell-step` (H19). As on
 * the two other boxes, a step that ALREADY carries the pair - a content posed by
 * hand or by the assistant - keeps the box live, since unticking is the only way
 * out of the refusal.
 *
 * The bubble hangs on `TooltipShield` for the reason `farewell-option.tsx`
 * gives: a disabled control receives neither pointer nor focus.
 *
 * No badge on the frise card (H105): the case reads in the step's own panel.
 * What the box changes INSIDE the panel is the pill of an objective's direction
 * (`objective-row.tsx`, H20), which says who reads it.
 */

export interface HideObjectivesOptionProps {
  readonly step: ProtocolStepDraft;
  readonly readOnly: boolean;
  readonly onChangeStep: (next: ProtocolStepDraft) => void;
}

/** Why the box is greyed - §4.3, word for word. */
export const HIDE_OBJECTIVES_BLOCKED_REASON = 'Une étape d’adieu n’a pas d’objectifs à cacher.';

export function HideObjectivesOption({ step, readOnly, onChangeStep }: HideObjectivesOptionProps) {
  const id = useId();
  // Blocked by the step's NATURE, which is what the bubble explains; read-only
  // is said above the whole editor and gets no bubble of its own.
  const blocked = step.farewell && !step.hideObjectives;
  const closed = readOnly || blocked;
  const explained = blocked && !readOnly;

  return (
    <Tooltip label={explained ? HIDE_OBJECTIVES_BLOCKED_REASON : undefined}>
      <TooltipShield closed={explained} className="items-start">
        <label
          htmlFor={id}
          className={cn(
            'flex items-start gap-2.5',
            closed ? 'cursor-not-allowed' : 'cursor-pointer',
          )}
        >
          <input
            id={id}
            type="checkbox"
            data-testid={PROTOCOL_TEST_IDS.stepHideObjectives}
            checked={step.hideObjectives}
            disabled={closed}
            onChange={(event) => {
              // A closed box writes nothing, whatever event arrives - the
              // guard `farewell-option.tsx` explains (jsdom still delivers a
              // synthetic click to a disabled input).
              if (closed) return;
              onChangeStep({ ...step, hideObjectives: event.currentTarget.checked });
            }}
            className="mt-1 size-4.5 shrink-0 accent-bleu-nuit disabled:cursor-not-allowed"
          />
          <span>
            <span
              className={cn(
                'block font-semibold text-sm',
                blocked ? 'text-gray-400' : 'text-gray-800',
              )}
            >
              Cacher les objectifs à l’interviewer
            </span>
            <span className={cn('block text-caption', blocked ? 'text-gray-400' : 'text-gray-600')}>
              Il ne reçoit que la direction de l’étape : ni les objectifs ouverts, ni ce qui leur
              manque. Le scribe les juge comme avant.
            </span>
          </span>
        </label>
      </TooltipShield>
    </Tooltip>
  );
}
